import { Link } from 'react-router-dom';
import { legalNav, nav, site, whatsappEnquiry } from '../data/site';
import { Icon, Ornament } from './Primitives';
import collectionsData from '../data/collections.json';

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="noise-navy relative overflow-hidden text-cream-100">
      {/* Oversized crest watermark */}
      <img
        src="/assets/logo/monogram-white.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-24 w-[28rem] opacity-[0.04]"
      />

      <div className="shell relative pb-10 pt-16 sm:pt-20">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-4">
            <img
              src="/assets/logo/logo-stacked-white.png"
              alt={`${site.name}, since ${site.since}`}
              width="900"
              height="1370"
              className="h-28 w-auto"
            />
            <p className="mt-6 max-w-sm text-[14.5px] leading-relaxed text-cream-200/70">
              Three generations on C G Road, supplying kundan, polki and diamond
              jewellery to families and to the trade across Gujarat.
            </p>
            <div className="mt-7 flex items-center gap-3">
              {[
                ['Instagram', site.social.instagram, Icon.Instagram],
                ['Facebook', site.social.facebook, Icon.Facebook],
                ['WhatsApp', whatsappEnquiry(), Icon.WhatsApp],
              ].map(([label, href, Glyph]) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={label}
                  className="grid h-10 w-10 place-items-center border border-cream-50/15 text-cream-200/80
                    transition-all duration-500 ease-luxe hover:border-gold-400 hover:bg-gold-500 hover:text-royal-900"
                >
                  <Glyph className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Explore */}
          <nav className="lg:col-span-2" aria-label="Footer">
            <h3 className="eyebrow-light">Explore</h3>
            <ul className="mt-5 space-y-3">
              {nav.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="link-underline text-[14.5px] text-cream-200/75 hover:text-gold-300"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Collections */}
          <nav className="lg:col-span-3" aria-label="Collections">
            <h3 className="eyebrow-light">Collections</h3>
            <ul className="mt-5 space-y-3">
              {collectionsData.collections.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/collections/${c.slug}`}
                    className="link-underline text-[14.5px] text-cream-200/75 hover:text-gold-300"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Visit */}
          <div className="lg:col-span-3">
            <h3 className="eyebrow-light">Visit the showroom</h3>
            <ul className="mt-5 space-y-4 text-[14.5px] text-cream-200/75">
              <li className="flex gap-3">
                <Icon.Pin className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" />
                <a
                  href={site.social.map}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="leading-relaxed hover:text-gold-300"
                >
                  {site.address.line1}, {site.address.line2},<br />
                  {site.address.city}, {site.address.state} {site.address.postalCode}
                </a>
              </li>
              <li className="flex gap-3">
                <Icon.Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" />
                <a href={site.phoneHref} className="hover:text-gold-300">
                  {site.phone}
                </a>
              </li>
              <li className="flex gap-3">
                <Icon.Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" />
                <a href={site.emailHref} className="break-all hover:text-gold-300">
                  {site.email}
                </a>
              </li>
              <li className="flex gap-3">
                <Icon.Clock className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" />
                <div className="leading-relaxed">
                  {site.hours.map((h) => (
                    <div key={h.days}>
                      <span className="text-cream-100">{h.days}</span>
                      <br />
                      {h.time}
                    </div>
                  ))}
                </div>
              </li>
            </ul>
          </div>
        </div>

        <Ornament tone="light" className="mt-14" />

        <div className="mt-8 space-y-5">
          <div className="flex flex-col items-center justify-between gap-4 text-[11.5px] uppercase tracking-wide2 text-cream-200/45 sm:flex-row">
            <p>
              © {year} {site.legalName} · Since {site.since}
            </p>
            <nav aria-label="Legal" className="flex items-center gap-5">
              {legalNav.map((item) => (
                <Link key={item.to} to={item.to} className="link-underline hover:text-gold-300">
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex flex-col items-center justify-between gap-3 border-t border-cream-50/[0.07] pt-5 text-[11.5px] uppercase tracking-wide2 text-cream-200/40 sm:flex-row">
            <p>Ahmedabad, Gujarat · Wholesalers &amp; Retailers</p>
            <p>
              Developed by{' '}
              <a
                href={site.developer.url}
                target="_blank"
                rel="noreferrer noopener"
                className="link-underline text-cream-200/70 hover:text-gold-300"
              >
                {site.developer.name}
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
